export const PLAYBOOK_PDF_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_playbook-path/artifacts/9kx7c8vo_The%2012-Step%20Authority%20Playbook_20260627_163509_0000.pdf";

export const PROGRAM_NAME = "RouteHaul Logistics Pathways";

export const PLAYBOOK_STEPS = [
  {
    n: 1,
    tag: "Foundation",
    title: "Business Entity Structure (LLC/Corp)",
    text: "File a formal corporate entity to protect your personal assets before leasing or buying equipment.",
    whatItIs:
      "A legal business structure — usually an LLC — registered with your state that makes your trucking operation its own separate legal entity, apart from you personally.",
    why:
      "Without it, your truck, home and savings are all one pot: one lawsuit or unpaid debt can wipe you out personally. It's also what lets you open business banking and build business credit.",
    note:
      "Running as a sole proprietor or mixing business and personal money feels easier at first — but one accident claim or unpaid fuel bill can then reach your house, savings and personal credit, because the law sees no separation between you and the business. This is one of the most common ways new carriers lose everything before they even get established.",
    overview:
      "Before you touch a truck or sign a lease, you need a legal firewall between your business and your personal assets. Forming an LLC or corporation limits your liability, makes you bankable, and is the foundation brokers and lenders expect to see.",
    tasks: [
      "Decide on your structure — most owner-operators start with an LLC.",
      "Register the entity with your state's Secretary of State.",
      "Draft an operating agreement, even as a single-member LLC.",
      "Keep business and personal finances strictly separate from day one.",
    ],
    resources: [
      { label: "SBA — Choose a Business Structure", url: "https://www.sba.gov/business-guide/launch-your-business/choose-business-structure", desc: "Compare LLC, S-Corp and sole proprietorship." },
      { label: "SBA — Launch Your Business Guide", url: "https://www.sba.gov/business-guide/launch-your-business", desc: "Step-by-step startup checklist from the Small Business Administration." },
      { label: "IRS — Business Structures", url: "https://www.irs.gov/businesses/small-businesses-self-employed/business-structures", desc: "How each structure is taxed." },
    ],
  },
  {
    n: 2,
    tag: "Foundation",
    title: "Commercial Address & EIN",
    text: "Secure a dedicated commercial business address and obtain your Federal Employer Identification Number.",
    whatItIs:
      "A legitimate business address on record with FMCSA, plus a free federal tax ID (EIN) issued by the IRS.",
    why:
      "The EIN is required to open a business bank account, file taxes, and register with FMCSA. A real commercial address keeps your carrier profile compliant and credible with brokers and lenders.",
    note:
      "Trying to shortcut this with a home address or P.O. box gets FMCSA filings rejected and freezes your banking. Carriers routinely stall for weeks because a rejected address forces them to redo every downstream filing — a small skipped step that snowballs into lost time and money.",
    overview:
      "Your EIN is your business's social security number — required for banking, taxes and FMCSA registration. Pair it with a legitimate commercial address so your carrier profile looks professional and compliant.",
    tasks: [
      "Establish a commercial or registered business address (not a P.O. box for FMCSA).",
      "Apply for your EIN directly with the IRS — it's free.",
      "Open a dedicated business checking account using the EIN.",
      "Register for any required state tax accounts.",
    ],
    resources: [
      { label: "IRS — Apply for an EIN Online (free)", url: "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online", desc: "Get your EIN in minutes at no cost." },
      { label: "IRS — Small Business & Self-Employed Center", url: "https://www.irs.gov/businesses/small-businesses-self-employed", desc: "Tax obligations for new businesses." },
    ],
  },
  {
    n: 3,
    tag: "Authority",
    title: "Federal DOT & MC Authority Filings",
    text: "Submit your FMCSA application, which initiates the mandatory 21-day federal protest period.",
    whatItIs:
      "Your federal license to run as a for-hire interstate carrier — a USDOT number plus MC operating authority granted by FMCSA.",
    why:
      "It's illegal to haul regulated freight across state lines for pay without it. This is the exact moment you officially become a recognized motor carrier.",
    note:
      "Hauling a load before your authority is active is operating illegally — brokers won't pay you, and FMCSA can fine you and revoke the pending authority. Many eager new operators grab 'just one load' during the 21-day protest window and end up losing the authority they paid for.",
    overview:
      "This is where you become a real, federally recognized motor carrier. You'll apply for your USDOT number and MC operating authority through FMCSA. Once filed, a 21-day protest period begins before your authority activates.",
    tasks: [
      "First-time applicants register through the Unified Registration System (URS).",
      "Pay the $300 fee per operating authority requested.",
      "Note your USDOT and MC numbers once issued.",
      "Track your application status on FMCSA Licensing & Insurance.",
    ],
    resources: [
      { label: "FMCSA — Get MC Number / Authority to Operate", url: "https://www.fmcsa.dot.gov/registration/get-mc-number-authority-operate", desc: "Official operating authority application." },
      { label: "FMCSA — Getting Started (Registration)", url: "https://www.fmcsa.dot.gov/registration/getting-started", desc: "The full registration roadmap." },
      { label: "FMCSA — Registration Forms", url: "https://www.fmcsa.dot.gov/registration/registration-forms", desc: "OP-1 and related forms." },
    ],
  },
  {
    n: 4,
    tag: "Authority",
    title: "BOC-3 & Commercial Insurance",
    text: "Designate legal process agents and secure insurance meeting the $750,000 minimum liability requirement.",
    whatItIs:
      "A BOC-3 filing names legal 'process agents' in every state so you can be served legal papers, and commercial insurance proves you can cover damages.",
    why:
      "FMCSA will not activate your authority until both are on file. No BOC-3 and no insurance means your MC number stays inactive and you can't legally take loads.",
    note:
      "Skipping the BOC-3 or buying the cheapest, thinnest insurance policy leaves your authority stuck 'pending' — you can't book a single legal load. Worse, a serious wreck while under-insured can bankrupt a brand-new carrier overnight, because the damages far exceed the coverage.",
    overview:
      "Your authority won't activate until two things are on file with FMCSA: a BOC-3 (process agents in every state) filed by a licensed agent, and proof of insurance meeting the federal minimum liability.",
    tasks: [
      "Hire a licensed process agent to file your BOC-3 with FMCSA.",
      "Purchase commercial auto liability meeting the $750,000 minimum.",
      "Have your insurer file proof of coverage with FMCSA.",
      "Confirm both filings appear on Licensing & Insurance.",
    ],
    resources: [
      { label: "FMCSA — Process Agents (BOC-3)", url: "https://www.fmcsa.dot.gov/registration/process-agents", desc: "How the BOC-3 filing works." },
      { label: "FMCSA — Insurance Filing Requirements", url: "https://www.fmcsa.dot.gov/registration/insurance-filing-requirements", desc: "Minimum liability limits by cargo type." },
      { label: "FMCSA — Licensing & Insurance Lookup", url: "https://li-public.fmcsa.dot.gov/", desc: "Verify your filings are on record." },
    ],
  },
  {
    n: 5,
    tag: "Compliance",
    title: "UCR & Drug & Alcohol Clearinghouse",
    text: "File your Unified Carrier Registration and register your company in the federal Clearinghouse portal.",
    whatItIs:
      "An annual interstate registration fee (UCR) based on fleet size, plus enrollment in the federal Drug & Alcohol Clearinghouse database.",
    why:
      "UCR is mandatory for interstate operation, and the Clearinghouse is where you must check every driver's drug and alcohol history — including your own — before they drive.",
    note:
      "Forget your UCR and a routine roadside inspection turns into fines and an out-of-service order. Skip a required Clearinghouse query and you've illegally let a prohibited driver — possibly yourself — operate, which is exactly the kind of violation that ends a young carrier's authority.",
    overview:
      "Interstate carriers must pay UCR fees annually based on fleet size, and every carrier with CDL drivers must be registered in the FMCSA Drug & Alcohol Clearinghouse to run queries.",
    tasks: [
      "File and pay your UCR based on your fleet size.",
      "Create your company profile in the Clearinghouse.",
      "Designate a Consortium/Third-Party Administrator (C/TPA) if needed.",
      "Run pre-employment and annual Clearinghouse queries.",
    ],
    resources: [
      { label: "UCR — Unified Carrier Registration", url: "https://www.ucr.gov/", desc: "Official UCR filing portal." },
      { label: "FMCSA — Drug & Alcohol Clearinghouse", url: "https://clearinghouse.fmcsa.dot.gov/", desc: "Register and run driver queries." },
    ],
  },
  {
    n: 6,
    tag: "Operations",
    title: "ELD & IFTA Infrastructure",
    text: "Install a compliant Electronic Logging Device and open a state IFTA account for mileage tracking.",
    whatItIs:
      "An Electronic Logging Device that automatically records your driving hours, plus an IFTA account for reporting fuel tax across the states you run.",
    why:
      "The ELD keeps you legal on hours-of-service and out of costly violations; IFTA ensures you pay — and get credited for — the right fuel taxes in each jurisdiction.",
    note:
      "Running paper logs or an unregistered ELD means automatic hours-of-service violations that tank your CSA score and scare off good brokers. Ignoring IFTA leads to surprise back-taxes and penalties that quietly drain the thin bank account a new carrier can't afford to lose.",
    overview:
      "Hours-of-service compliance requires a registered ELD, and interstate fuel tax reporting requires an IFTA license and decals from your base state. Get both running before your first load.",
    tasks: [
      "Choose an ELD from the FMCSA registered devices list.",
      "Install and test the ELD before operating.",
      "Apply for your IFTA license through your base state.",
      "Set a routine for logging miles and fuel by jurisdiction.",
    ],
    resources: [
      { label: "FMCSA — Electronic Logging Devices (ELD)", url: "https://www.fmcsa.dot.gov/hours-service/elds/electronic-logging-devices", desc: "Rules and registered device list." },
      { label: "IFTA, Inc. — International Fuel Tax Agreement", url: "https://www.iftach.org/", desc: "IFTA rules and member jurisdictions." },
    ],
  },
  {
    n: 7,
    tag: "Compliance",
    title: "Driver Qualification (DQ) File",
    text: "Build the mandatory folder: commercial applications, MVR background checks and medical certificates.",
    whatItIs:
      "A required file of driver documents — application, motor vehicle record, medical card and more — kept for every driver, including you as the owner-operator.",
    why:
      "It's one of the first things a DOT auditor asks to see. An incomplete DQ file is an easy, entirely avoidable violation that can sink your safety rating.",
    note:
      "An incomplete DQ file is the easiest violation an auditor can write up. New carriers fail their safety audit over a missing medical card or MVR — paperwork that took ten minutes to file correctly — and lose the authority they just fought to earn.",
    overview:
      "Every driver — including you — needs a complete Driver Qualification file. It's one of the first things a safety auditor checks, so build it correctly from the start.",
    tasks: [
      "Complete a compliant driver application.",
      "Pull the Motor Vehicle Record (MVR) and annual review.",
      "File the current DOT medical examiner's certificate.",
      "Keep road test / CDL verification on record.",
    ],
    resources: [
      { label: "FMCSA — §391.51 DQ File Requirements", url: "https://www.fmcsa.dot.gov/regulations/title/49/section/391.51", desc: "Exactly what the file must contain." },
      { label: "FMCSA — Regulations (Part 391)", url: "https://www.fmcsa.dot.gov/regulations/title/49/part/391", desc: "Driver qualification regulations." },
    ],
  },
  {
    n: 8,
    tag: "Compliance",
    title: "Pre-Employment Drug Testing Consortium",
    text: "Join a certified drug-testing consortium and get a verified negative result before operating.",
    whatItIs:
      "Enrollment in a DOT-certified drug & alcohol testing consortium (C/TPA) and a passing pre-employment test before anyone gets behind the wheel.",
    why:
      "Federal law bans operating a commercial vehicle without it. Skipping the test or the random pool risks heavy fines and being placed out of service.",
    note:
      "Putting anyone — including yourself — on the road before a verified negative test and random-pool enrollment is a serious federal violation. It's an instant out-of-service order and fines heavy enough to end a startup carrier before its first profitable month.",
    overview:
      "Federal law requires a negative pre-employment drug test and enrollment in a random testing program before any driver operates a CMV. A consortium (C/TPA) manages this for small carriers.",
    tasks: [
      "Enroll in a DOT-certified drug & alcohol consortium.",
      "Complete a pre-employment test with a verified negative.",
      "Stay in the random testing pool year-round.",
      "Report results through the Clearinghouse.",
    ],
    resources: [
      { label: "FMCSA — Drug & Alcohol Testing Program", url: "https://www.fmcsa.dot.gov/regulations/drug-alcohol-testing-program", desc: "Testing rules and requirements." },
      { label: "FMCSA — Drug & Alcohol Clearinghouse", url: "https://clearinghouse.fmcsa.dot.gov/", desc: "Where results are recorded." },
    ],
  },
  {
    n: 9,
    tag: "Safety",
    title: "New Entrant Safety Audit Prep",
    text: "Organize maintenance, log and training records digitally to pass the FMCSA audit in year one.",
    whatItIs:
      "A 12-month monitoring period for every brand-new carrier that ends in an FMCSA safety audit of your records and processes.",
    why:
      "Fail the audit and your brand-new authority can be revoked. Keeping records organized from day one turns the audit into a formality instead of a crisis.",
    note:
      "Disorganized records are the number-one reason new carriers fail the 12-month safety audit and lose the authority they just paid for. The audit itself isn't hard — it's the last-minute scramble to find missing maintenance, log and testing records that sinks people.",
    overview:
      "New carriers are monitored for 12 months and must pass a safety audit. Keep maintenance records, ELD logs, DQ files and testing records organized so the audit is a formality — not a fire drill.",
    tasks: [
      "Maintain a digital system for all compliance records.",
      "Keep vehicle maintenance and DVIR logs current.",
      "Retain ELD/HOS records per retention rules.",
      "Prepare for the audit within your first 12 months.",
    ],
    resources: [
      { label: "FMCSA — New Entrant Safety Assurance", url: "https://www.fmcsa.dot.gov/registration/getting-started", desc: "How the 12-month monitoring works." },
      { label: "SAFER — Company Snapshot", url: "https://safer.fmcsa.dot.gov/", desc: "Check your public safety profile." },
    ],
  },
  {
    n: 10,
    tag: "Financials",
    title: "Broker Vetting & Factoring Setup",
    text: "Establish a factoring account to offset the standard 30-day broker payment delays on your cash flow.",
    whatItIs:
      "Checking a broker's credit and status before you haul for them, plus using a factoring company that pays you for invoices right away instead of in 30+ days.",
    why:
      "Brokers typically pay slowly and some don't pay at all. Factoring keeps cash flowing week to week, and vetting protects you from hauling for free.",
    note:
      "Hauling for an unvetted broker who pays in 45 days — or never — while your truck payment is due is the classic cash-flow trap. Plenty of carriers are profitable on paper but shut down anyway because the money arrives too late to cover the bills due right now.",
    overview:
      "Brokers typically pay in 30+ days, which kills cash flow for a new carrier. Factoring advances your invoice cash immediately, while vetting brokers protects you from non-payment.",
    tasks: [
      "Open a factoring account before your first load.",
      "Vet every broker's credit and safety record before hauling.",
      "Track receivables and days-to-pay by broker.",
      "Keep a cash reserve for slow weeks.",
    ],
    resources: [
      { label: "SAFER — Verify a Broker / Carrier", url: "https://safer.fmcsa.dot.gov/", desc: "Confirm authority and status before you haul." },
      { label: "SBA — Fund Your Business", url: "https://www.sba.gov/business-guide/plan-your-business/fund-your-business", desc: "Financing options for cash flow." },
    ],
  },
  {
    n: 11,
    tag: "Growth",
    title: "Regional Lane Optimization",
    text: "Map operating routes around geography and mountain freight rules to maximize revenue per mile.",
    whatItIs:
      "Deliberately choosing which routes and regions you run based on rate-per-mile, fuel cost, terrain and empty (deadhead) miles.",
    why:
      "Being busy isn't the same as being profitable. Smart lane selection maximizes your revenue per mile and protects your margins from bad, low-paying freight.",
    note:
      "Chasing every cheap load just to 'stay busy' burns fuel, hours and truck wear on runs that lose money once you subtract expenses. Many owner-operators quit exhausted and broke, never realizing that high miles do not equal high profit.",
    overview:
      "Not all miles are equal. Plan lanes that balance rate-per-mile, deadhead, fuel cost and terrain so your early runs are profitable, not just busy.",
    tasks: [
      "Identify high-density freight lanes near your base.",
      "Account for terrain, tolls and seasonal hazards.",
      "Minimize deadhead by planning backhauls.",
      "Track revenue per mile on every lane.",
    ],
    resources: [
      { label: "FMCSA — Hours of Service Rules", url: "https://www.fmcsa.dot.gov/regulations/hours-of-service", desc: "Plan legal, efficient routes." },
      { label: "BTS — Freight Facts & Figures", url: "https://www.bts.gov/product/freight-facts-and-figures", desc: "National freight movement data." },
    ],
  },
  {
    n: 12,
    tag: "Growth",
    title: "Nationwide Dispatch Operations",
    text: "Transition into a scalable, structured national dispatch framework for safe, compliant operations.",
    whatItIs:
      "Scaling from running solo into structured, repeatable dispatch and back-office operations that work across the whole country.",
    why:
      "Growth breaks businesses that don't have systems. Structure lets you add lanes, drivers and trucks without losing compliance — or your profit.",
    note:
      "Adding trucks and lanes without systems is how carriers grow straight into bankruptcy: compliance slips, cash flow breaks, and a single CSA spike from one bad hire can ground the entire fleet. Scaling on hustle alone eventually collapses under its own weight.",
    overview:
      "Once your foundation is solid, scale with structured dispatch, back-office systems and repeatable processes so growth doesn't break your compliance or your margins.",
    tasks: [
      "Systematize dispatch, billing and compliance workflows.",
      "Build direct shipper and broker relationships.",
      "Monitor CSA scores as you add lanes and equipment.",
      "Consider delegating operations so you can focus on growth.",
    ],
    resources: [
      { label: "FMCSA — Safety Management Cycle", url: "https://www.fmcsa.dot.gov/safety", desc: "Keep safety strong while scaling." },
      { label: "SBA — Grow Your Business", url: "https://www.sba.gov/business-guide/grow-your-business", desc: "Scaling resources for small carriers." },
    ],
  },
];

export const getStep = (n) => PLAYBOOK_STEPS.find((s) => s.n === Number(n));
